import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { PrimaryCamper, CAMPERS_COLLECTION } from '../../../types/camper';
import './CamperList.css';

interface CamperWithLastStay extends PrimaryCamper {
  lastStay?: {
    year: number;
    inventoryName: string;
  };
}

const CamperList: React.FC = () => {
  const navigate = useNavigate();
  const [campers, setCampers] = useState<CamperWithLastStay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchCampers = async () => {
      try {
        setLoading(true);
        setError('');

        console.log('Fetching campers from Firestore...');
        // Query only non-deleted campers
        const campersQuery = query(
          collection(db, CAMPERS_COLLECTION),
          where('deleted', '==', false)
        );
        
        console.log('Executing campers query...');
        const snapshot = await getDocs(campersQuery);
        console.log(`Query returned ${snapshot.size} documents`);
        
        // Log all documents for debugging
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log(`Camper ID: ${doc.id}, FirstName: ${data.FirstName}, LastName: ${data.LastName}, deleted: ${data.deleted}`);
        });
        
        const campersData = snapshot.docs
          .map(doc => ({
            ...doc.data() as PrimaryCamper,
            recordId: doc.id
          }))
          .sort((a, b) => (a.LastName || '').localeCompare(b.LastName || '')); // Sort in memory, with null check

        console.log(`Processed ${campersData.length} campers for display`);
        setCampers(campersData);
      } catch (err) {
        console.error('Error fetching campers:', err);
        setError('Failed to load campers');
      } finally {
        setLoading(false);
      }
    };

    fetchCampers();
  }, []);

  const handleEditCamper = (camperId: string) => {
    navigate(`/admin/campers/${camperId}`);
  };

  const handleAddNewCamper = () => {
    navigate('/admin/campers/new');
  };

  const handleMergeRecords = () => {
    navigate('/admin/campers/merge-records');
  };

  // Filter campers based on search query
  const filteredCampers = campers.filter(camper => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Check primary camper's name
    if ((camper.LastName || '').toLowerCase().includes(query) ||
        (camper.FirstName || '').toLowerCase().includes(query)) {
      return true;
    }
    
    // Check dependants' names
    return camper.dependants?.some(dep =>
      (dep.LastName || '').toLowerCase().includes(query) ||
      (dep.FirstName || '').toLowerCase().includes(query)
    ) || false;
  });

  if (loading) {
    return (
      <div className="camper-list-container">
        <div className="loading">Loading campers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camper-list-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="camper-list-container">
      <div className="header">
        <h1>Camper Management</h1>
        <div className="header-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name (includes dependants)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button onClick={handleAddNewCamper} className="add-camper-button">
            Add New Camper
          </button>
          <button onClick={handleMergeRecords} className="merge-camper-button">
            Merge 2 Camper Records
          </button>
        </div>
      </div>

      <div className="camper-list">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCampers.map((camper) => (
              <tr 
                key={camper.recordId}
                onClick={() => handleEditCamper(camper.recordId)}
                className="clickable-row"
              >
                <td>{`${camper.FirstName || ''} ${camper.LastName || ''}`}</td>
                <td>{camper.email || ''}</td>
                <td>{camper.MobilePhone || camper.HomePhone || ''}</td>
                <td>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCamper(camper.recordId);
                    }}
                    className="edit-button"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCampers.length === 0 && searchQuery && (
          <div className="no-campers">
            No campers found matching "{searchQuery}"
          </div>
        )}
        {filteredCampers.length === 0 && !searchQuery && (
          <div className="no-campers">
            No campers found. Click "Add New Camper" to create one.
          </div>
        )}
      </div>
    </div>
  );
};

export default CamperList; 